import React, { useState } from 'react';
import { useLabs, useLabCategories, useToggleLabPublish, useDeleteLab, useCreateLab, useUpdateLab } from '../hooks';
import { useToast } from '../components/Toast';
import type { Lab } from '../types';
import styles from './Labs.module.css';

interface LabFormData {
  title: string;
  description: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number;
  instructions: string;
  objectives: string[];
  prerequisites: string[];
  isPublished: boolean;
}

const defaultFormData: LabFormData = {
  title: '',
  description: '',
  category: '',
  difficulty: 'beginner',
  estimatedDuration: 60,
  instructions: '',
  objectives: [''],
  prerequisites: [''],
  isPublished: false,
};

const Labs: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [publishedFilter, setPublishedFilter] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showLabModal, setShowLabModal] = useState(false);
  const [editingLab, setEditingLab] = useState<Lab | null>(null);
  const [formData, setFormData] = useState<LabFormData>(defaultFormData);
  const toast = useToast();

  const { data, isLoading, isError, refetch } = useLabs({
    search: searchQuery || undefined,
    category: categoryFilter || undefined,
    difficulty: difficultyFilter as Lab['difficulty'] | undefined,
    isPublished: publishedFilter ? publishedFilter === 'published' : undefined,
  });

  const { data: categories } = useLabCategories();
  const togglePublish = useToggleLabPublish();
  const deleteLab = useDeleteLab();
  const createLab = useCreateLab();
  const updateLab = useUpdateLab();

  const labs = data?.data || [];

  const openCreateModal = () => {
    setFormData(defaultFormData);
    setEditingLab(null);
    setShowLabModal(true);
  };

  const openEditModal = (lab: Lab) => {
    setFormData({
      title: lab.title,
      description: lab.description,
      category: lab.category,
      difficulty: lab.difficulty,
      estimatedDuration: lab.estimatedDuration,
      instructions: lab.instructions || '',
      objectives: lab.objectives?.length ? lab.objectives : [''],
      prerequisites: lab.prerequisites?.length ? lab.prerequisites : [''],
      isPublished: lab.isPublished,
    });
    setEditingLab(lab);
    setShowLabModal(true);
  };

  const closeModal = () => {
    setShowLabModal(false);
    setEditingLab(null);
    setFormData(defaultFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    const labData = {
      ...formData,
      objectives: formData.objectives.filter(o => o.trim()),
      prerequisites: formData.prerequisites.filter(p => p.trim()),
    };

    try {
      if (editingLab) {
        await updateLab.mutateAsync({ labId: editingLab.id, data: labData });
        toast.showSuccess('Lab updated successfully');
      } else {
        await createLab.mutateAsync(labData);
        toast.showSuccess('Lab created successfully');
      }
      closeModal();
      refetch();
    } catch (error) {
      console.error('Failed to save lab:', error);
      toast.showError(editingLab ? 'Failed to update lab' : 'Failed to create lab');
    }
  };

  const addObjective = () => {
    setFormData(prev => ({ ...prev, objectives: [...prev.objectives, ''] }));
  };

  const removeObjective = (index: number) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index),
    }));
  };

  const updateObjective = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      objectives: prev.objectives.map((o, i) => i === index ? value : o),
    }));
  };

  const addPrerequisite = () => {
    setFormData(prev => ({ ...prev, prerequisites: [...prev.prerequisites, ''] }));
  };

  const removePrerequisite = (index: number) => {
    setFormData(prev => ({
      ...prev,
      prerequisites: prev.prerequisites.filter((_, i) => i !== index),
    }));
  };

  const updatePrerequisite = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      prerequisites: prev.prerequisites.map((p, i) => i === index ? value : p),
    }));
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return styles.beginner;
      case 'intermediate':
        return styles.intermediate;
      case 'advanced':
        return styles.advanced;
      default:
        return '';
    }
  };

  const handleTogglePublish = async (labId: string, currentlyPublished: boolean) => {
    try {
      await togglePublish.mutateAsync({ labId, publish: !currentlyPublished });
      toast.showSuccess(currentlyPublished ? 'Lab unpublished' : 'Lab published');
      refetch();
    } catch (error) {
      console.error('Failed to toggle publish:', error);
      toast.showError('Failed to update publish status');
    }
  };

  const handleDelete = async (labId: string) => {
    try {
      await deleteLab.mutateAsync(labId);
      toast.showSuccess('Lab deleted successfully');
      setDeleteConfirm(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete lab:', error);
      toast.showError('Failed to delete lab');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <span>Loading labs...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.loading}>
        <span>Failed to load labs</span>
        <button className={styles.createBtn} onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Labs</h1>
          <p>Manage training labs and content</p>
        </div>
        <button className={styles.createBtn} onClick={openCreateModal}>
          <span>+</span> Create New Lab
        </button>
      </header>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>search</span>
          <input
            type="text"
            placeholder="Search labs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button className={styles.clearBtn} onClick={() => setSearchQuery('')}>
              x
            </button>
          )}
        </div>

        <div className={styles.filterGroup}>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">All Categories</option>
            {categories?.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">All Difficulties</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <select
            value={publishedFilter}
            onChange={(e) => setPublishedFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Stats Summary */}
      <div className={styles.statsSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{labs.length}</span>
          <span className={styles.summaryLabel}>Total Labs</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>
            {labs.filter((l) => l.isPublished).length}
          </span>
          <span className={styles.summaryLabel}>Published</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>
            {labs.reduce((sum, l) => sum + l.studentsInProgress, 0)}
          </span>
          <span className={styles.summaryLabel}>Active Students</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>
            {labs.reduce((sum, l) => sum + l.submissionsPending, 0)}
          </span>
          <span className={styles.summaryLabel}>Pending Reviews</span>
        </div>
      </div>

      {/* Labs Grid */}
      {labs.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>science</span>
          <p>No labs found</p>
          {searchQuery && <span>Try adjusting your search criteria</span>}
        </div>
      ) : (
        <div className={styles.grid}>
          {labs.map((lab) => (
            <div key={lab.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={`${styles.difficulty} ${getDifficultyColor(lab.difficulty)}`}>
                  {lab.difficulty}
                </span>
                <span className={lab.isPublished ? styles.published : styles.draft}>
                  {lab.isPublished ? 'Published' : 'Draft'}
                </span>
              </div>

              <h3 className={styles.cardTitle}>{lab.title}</h3>
              <p className={styles.description}>{lab.description}</p>
              <p className={styles.category}>
                <span className={styles.categoryIcon}>folder</span>
                {lab.category} | {lab.courseTitle}
              </p>

              <div className={styles.metaRow}>
                <span className={styles.duration}>
                  <span className={styles.durationIcon}>schedule</span>
                  {lab.estimatedDuration} min
                </span>
                {lab.averageScore > 0 && (
                  <span className={styles.avgScore}>Avg: {lab.averageScore}%</span>
                )}
              </div>

              <div className={styles.stats}>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{lab.studentsCompleted}</span>
                  <span className={styles.statLabel}>Completed</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{lab.studentsInProgress}</span>
                  <span className={styles.statLabel}>In Progress</span>
                </div>
                <div className={styles.stat}>
                  <span className={`${styles.statValue} ${lab.submissionsPending > 0 ? styles.pending : ''}`}>
                    {lab.submissionsPending}
                  </span>
                  <span className={styles.statLabel}>Pending</span>
                </div>
              </div>

              {lab.passRate > 0 && (
                <div className={styles.progressRow}>
                  <span className={styles.passLabel}>Pass Rate</span>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${lab.passRate}%` }}
                    />
                  </div>
                  <span className={styles.passRate}>{lab.passRate}%</span>
                </div>
              )}

              <div className={styles.cardActions}>
                <button className={styles.cardBtn} onClick={() => openEditModal(lab)}>
                  <span>edit</span>
                  Edit
                </button>
                <button
                  className={styles.cardBtn}
                  onClick={() => handleTogglePublish(lab.id, lab.isPublished)}
                  disabled={togglePublish.isPending}
                >
                  {lab.isPublished ? (
                    <>
                      <span>visibility_off</span>
                      Unpublish
                    </>
                  ) : (
                    <>
                      <span>visibility</span>
                      Publish
                    </>
                  )}
                </button>
                <button
                  className={`${styles.cardBtn} ${styles.danger}`}
                  onClick={() => setDeleteConfirm(lab.id)}
                >
                  <span>delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <span>warning</span>
            </div>
            <h3>Delete Lab?</h3>
            <p>
              This action cannot be undone. All student progress and submissions for this lab
              will be permanently removed.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                className={styles.deleteBtn}
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleteLab.isPending}
              >
                {deleteLab.isPending ? 'Deleting...' : 'Delete Lab'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Lab Modal */}
      {showLabModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.labModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingLab ? 'Edit Lab' : 'Create New Lab'}</h2>
              <button className={styles.closeBtn} onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.labForm}>
              <div className={styles.formSection}>
                <h3>Basic Information</h3>

                <div className={styles.formGroup}>
                  <label htmlFor="title">Title *</label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter lab title..."
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what students will learn..."
                    rows={3}
                    required
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="category">Category</label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="">Select category...</option>
                      {categories?.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="difficulty">Difficulty</label>
                    <select
                      id="difficulty"
                      value={formData.difficulty}
                      onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as LabFormData['difficulty'] })}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="duration">Duration (min)</label>
                    <input
                      id="duration"
                      type="number"
                      min="15"
                      max="480"
                      value={formData.estimatedDuration}
                      onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <h3>Learning Objectives</h3>
                <p className={styles.sectionHint}>What will students learn from this lab?</p>

                {formData.objectives.map((objective, index) => (
                  <div key={index} className={styles.listItem}>
                    <input
                      type="text"
                      value={objective}
                      onChange={(e) => updateObjective(index, e.target.value)}
                      placeholder={`Objective ${index + 1}...`}
                    />
                    {formData.objectives.length > 1 && (
                      <button type="button" className={styles.removeBtn} onClick={() => removeObjective(index)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className={styles.addBtn} onClick={addObjective}>
                  + Add Objective
                </button>
              </div>

              <div className={styles.formSection}>
                <h3>Prerequisites</h3>
                <p className={styles.sectionHint}>What should students know before starting?</p>

                {formData.prerequisites.map((prereq, index) => (
                  <div key={index} className={styles.listItem}>
                    <input
                      type="text"
                      value={prereq}
                      onChange={(e) => updatePrerequisite(index, e.target.value)}
                      placeholder={`Prerequisite ${index + 1}...`}
                    />
                    {formData.prerequisites.length > 1 && (
                      <button type="button" className={styles.removeBtn} onClick={() => removePrerequisite(index)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className={styles.addBtn} onClick={addPrerequisite}>
                  + Add Prerequisite
                </button>
              </div>

              <div className={styles.formSection}>
                <h3>Lab Instructions</h3>
                <div className={styles.formGroup}>
                  <label htmlFor="instructions">Instructions (Markdown supported)</label>
                  <textarea
                    id="instructions"
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    placeholder="Write detailed instructions for the lab..."
                    rows={8}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={formData.isPublished}
                      onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                    />
                    <span className={styles.checkmark}></span>
                    Publish immediately
                  </label>
                  <p className={styles.checkboxHint}>Published labs are visible to students</p>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelBtn} onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={createLab.isPending || updateLab.isPending}
                >
                  {(createLab.isPending || updateLab.isPending) ? (
                    'Saving...'
                  ) : editingLab ? (
                    'Update Lab'
                  ) : (
                    'Create Lab'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Labs;
